import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaBars, FaBell, FaSignOutAlt } from "react-icons/fa";
import "./Header.css";

const AdminHeader = ({ setSidebarOpen }) => {
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);

  // Generate breadcrumbs based on current path
  const getBreadcrumbs = () => {
    const pathnames = location.pathname.split('/').filter(x => x);
    return pathnames.map((name, index) => {
      const routeTo = `/${pathnames.slice(0, index + 1).join('/')}`;
      const isLast = index === pathnames.length - 1;
      const formattedName = name.charAt(0).toUpperCase() + name.slice(1);
      
      return isLast ? ( 
        <span key={routeTo} className="breadcrumb-active">
          {formattedName}
        </span>
      ) : (
        <React.Fragment key={routeTo}>
          <a href={routeTo} className="breadcrumb-link">
            {formattedName}
          </a>
          <span className="breadcrumb-separator">/</span>
        </React.Fragment>
      );
    });
  };

  // Fetch notifications for admin
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/chat/admin-notifications`);
        if (!response.ok) throw new Error("Failed to fetch notifications.");
        const data = await response.json();
        setNotifications(data);
      } catch (error) {
        console.error("Error fetching notifications:", error.message);
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5000);
    return () => clearInterval(interval);
  }, []);

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/login");
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          !event.target.closest('.notification-btn')) {
        setShowDropdown(false);
      }
    };

    // Add event listener when dropdown is shown
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    // Clean up event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Mark notifications as read
  const markNotificationsAsRead = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/chat/mark-as-read`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ userType: 'admin' }), 
      });
  
      if (!response.ok) {
        throw new Error("Failed to mark notifications as read.");
      }
  
      setNotifications([]);
      setShowDropdown(false);
    } catch (error) {
      console.error("Error marking notifications as read:", error.message);
    }
  };

  return (
    <header className="dashboard-header">
      <button className="menu-btn" onClick={() => setSidebarOpen(true)}>
        <FaBars />
      </button>
      
      <div className="breadcrumb-container">
        {getBreadcrumbs()}
      </div>

      <div className="header-right">
        <div className="notification-container">
          <button className="notification-btn" onClick={() => setShowDropdown(!showDropdown)}>
            <FaBell />
            {notifications.length > 0 && <span className="notification-badge">{notifications.length}</span>}
          </button>

          {showDropdown && (
            <div className="notification-dropdown" ref={dropdownRef}>
              {notifications.length > 0 ? (
                notifications.map((notif, index) => (
                  <div key={index} className="notification-item">
                    {notif.message}
                  </div>
                ))
              ) : (
                <div className="notification-item">No new notifications</div>
              )}
              <button onClick={markNotificationsAsRead} className="mark-as-read-btn">
                Mark as Read
              </button>
            </div>
          )}
        </div>

        <button className="logout-btn" onClick={handleLogout}>
          <FaSignOutAlt /> Logout
        </button>
      </div>
    </header>
  );
};

export default AdminHeader;